import { getFolders, getOrderedChildren } from './collectionFolders';

/**
 * Build the order-only payload for PUT /workspaces/:id/sidebar-layout.
 * Kept for back-compat; prefer buildTreeLayoutPayload which also persists folders.
 */
export function buildSidebarLayoutPayload(nextCollections) {
  return {
    collections: (nextCollections || []).map((col, colIndex) => ({
      id: col.id,
      sort_order: colIndex,
      requests: [...(col.requests || [])]
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .map((req, reqIndex) => ({
          id: req.id,
          sort_order: reqIndex,
        })),
    })),
  };
}

/**
 * Build the full-tree payload for PUT /workspaces/:id/sidebar-layout.
 *
 * Captures, in one request, the entire sidebar state the server must persist:
 *  - collection order (`sort_order`)
 *  - folder nesting + order (`folders: [{ id, parent_id, sort_order, name }]`)
 *  - each request's folder + order (`requests: [{ id, folder_id, sort_order }]`)
 *
 * Folder and request `sort_order` are recomputed **per parent scope** (root + each
 * folder) from getOrderedChildren, so the unified folders+requests ordering is sent
 * cleanly even if the in-memory sort_order values have gaps after a drag.
 */
export function buildTreeLayoutPayload(nextCollections) {
  return {
    collections: (nextCollections || []).map((col, colIndex) => {
      const folders = getFolders(col);
      const parentScopes = [null, ...folders.map((f) => String(f.id))];

      const folderOut = [];
      const requestOut = [];

      for (const parentId of parentScopes) {
        getOrderedChildren(col, parentId).forEach((entry, index) => {
          if (entry.type === 'folder') {
            folderOut.push({
              id: entry.id,
              parent_id: parentId,
              sort_order: index,
              name: entry.item.name,
            });
          } else {
            requestOut.push({
              id: entry.id,
              folder_id: parentId,
              sort_order: index,
            });
          }
        });
      }

      return {
        id: col.id,
        sort_order: colIndex,
        folders: folderOut,
        requests: requestOut,
      };
    }),
  };
}
